import * as React from 'react';
import { Route } from 'react-router';
import type { ChangeHook } from 'react-router';

import { isAuthenticatedUser } from 'js/lib/user';

import { Route as CourseraRoute } from '@coursera/react-router';

import loadOnRoute from 'bundles/common/components/loadOnRoute';
import NotFound from 'bundles/phoenix/components/NotFound';
import {
  CollectionLegacyRedirect,
  ViewAllBrowseRedirect,
} from 'bundles/unified-home-common/pages/org-scoped/view-all-collections/OrgCollectionRedirects';

const OrganizationHomePageRoot = loadOnRoute(
  () => import('bundles/unified-home-common/pages/org-scoped/home/OrganizationHomePageRoot')
);
const OrganizationMyLearningRoot = loadOnRoute(
  () => import('bundles/unified-home-common/pages/org-scoped/my-learning/OrganizationMyLearningPageRoot')
);
const OrganizationSkillsetsRoot = loadOnRoute(
  () => import('bundles/unified-home-common/pages/org-scoped/skillsets/OrganizationSkillsetsPageRoot')
);
const OrganizationOnboardingRoot = loadOnRoute(
  () => import('bundles/unified-home-common/pages/org-scoped/onboarding/OrganizationOnboardingPageRoot')
);
const OrganizationCollectionViewAllRoot = loadOnRoute(
  () =>
    import('bundles/unified-home-common/pages/org-scoped/view-all-collections/OrganizationCollectionViewAllPageRoot')
);

const scrollToTopOnNavigate: ChangeHook = (prevState, nextState) => {
  if (nextState.location.action !== 'PUSH') {
    // history navigation
    return;
  }

  if (nextState.location.pathname !== prevState.location.pathname) {
    // always on path navigation
    window.scrollTo(0, 0);
  }
};

export default (
  <CourseraRoute
    onChange={scrollToTopOnNavigate}
    handle={{
      tracking: {
        data: (params) => ({
          page: { segment: 'enterprise' },
          enterprise: {
            ...(params?.orgSlug ? { organizationSlug: params.orgSlug } : {}),
          },
        }),
      },
    }}
  >
    <Route path="/">
      <CourseraRoute
        path="organizations/:orgSlug"
        getComponent={OrganizationHomePageRoot}
        handle={{
          tracking: {
            data: () => ({
              page: { type: isAuthenticatedUser() ? 'lihp' : 'lohp' },
            }),
          },
        }}
      />
      <CourseraRoute
        path="organizations/:orgSlug/my-learning"
        getComponent={OrganizationMyLearningRoot}
        handle={{
          tracking: {
            data: () => ({
              page: { type: 'lihp_my_learnings' },
            }),
          },
        }}
      />
      <CourseraRoute
        path="organizations/:orgSlug/skillsets"
        getComponent={OrganizationSkillsetsRoot}
        handle={{
          tracking: {
            data: () => ({
              page: { type: 'lihp_skillsets' },
            }),
          },
        }}
      />
      <Route path="organizations/:orgSlug/onboarding" getComponent={OrganizationOnboardingRoot} />
      <Route path="organizations/:orgSlug/view-all-collections/browse" component={ViewAllBrowseRedirect} />
      <CourseraRoute
        path="organizations/:orgSlug/view-all-collections(/:collectionType)"
        getComponent={OrganizationCollectionViewAllRoot}
        handle={{
          tracking: {
            data: (_params, _location) => ({
              page: { type: 'lihp' },
            }),
          },
        }}
      />
      <Route path="organizations/:orgSlug/collections/:contextType/:contextId" component={CollectionLegacyRedirect} />
      <Route path="organizations/:orgSlug/collections/:collectionId" component={CollectionLegacyRedirect} />
      {/* This catch-all *, status=404 route is important for SSR. */}
      <Route path="*" component={NotFound} />
    </Route>
  </CourseraRoute>
);
